"use client";

import * as React from "react";
import type { CSSProperties } from "react";
import type { LucideIcon } from "lucide-react";
import { useRouter } from "next/router";
import { cn } from "@/lib/utils";
import { CIRCLE_ACTION_VARIANT_STYLES, type CircleActionVariant } from "./circle-action-variants";
import { LiquidPill } from "./liquid-pill";
import { useSidebarNavigation } from "./sidebar/use-sidebar-navigation";

export interface PageHeaderV2Particle {
  top: string;
  left: string;
  size: number;
  opacity: number;
}

export type PageHeaderV2Config = Omit<
  PageHeaderV2Props,
  "rightAction"
>;

export interface PageHeaderV2Props {
  title?: React.ReactNode;
  description?: React.ReactNode;
  leadingIcon?: React.ReactNode;
  titleSuffix?: React.ReactNode;
  /** @deprecated Header actions belong in FAB/toolbar — not rendered */
  rightAction?: React.ReactNode;
  badges?: React.ReactNode;
  showParticles?: boolean;
  particleItems?: PageHeaderV2Particle[];
  particleIcon?: LucideIcon;
  /** Gradient + particles only — hides title, description, icon and badges. */
  decorativeOnly?: boolean;
  /** Tighter bottom spacing when paired with headerBlend layouts. */
  compact?: boolean;
}

const DEFAULT_PARTICLES: PageHeaderV2Particle[] = [
  { top: "8%", left: "4%", size: 7, opacity: 0.12 },
  { top: "10%", left: "20%", size: 20, opacity: 0.26 },
  { top: "8%", left: "38%", size: 9, opacity: 0.15 },
  { top: "11%", left: "58%", size: 22, opacity: 0.24 },
  { top: "8%", left: "78%", size: 10, opacity: 0.16 },
  { top: "11%", left: "96%", size: 17, opacity: 0.19 },
  { top: "31%", left: "10%", size: 18, opacity: 0.22 },
  { top: "34%", left: "30%", size: 6, opacity: 0.11 },
  { top: "30%", left: "50%", size: 21, opacity: 0.23 },
  { top: "34%", left: "70%", size: 11, opacity: 0.17 },
  { top: "31%", left: "90%", size: 15, opacity: 0.20 },
  { top: "54%", left: "3%", size: 12, opacity: 0.15 },
  { top: "56%", left: "24%", size: 23, opacity: 0.22 },
  { top: "53%", left: "45%", size: 8, opacity: 0.14 },
  { top: "57%", left: "66%", size: 18, opacity: 0.20 },
  { top: "53%", left: "88%", size: 10, opacity: 0.16 },
  { top: "78%", left: "8%", size: 19, opacity: 0.18 },
  { top: "80%", left: "32%", size: 8, opacity: 0.13 },
  { top: "77%", left: "56%", size: 14, opacity: 0.16 },
  { top: "81%", left: "80%", size: 23, opacity: 0.21 },
  { top: "80%", left: "97%", size: 16, opacity: 0.17 },
];

function normalizeRoutePath(path?: string) {
  if (!path) return "";
  const [withoutHash] = path.split("#");
  const [withoutQuery] = withoutHash.split("?");
  const normalized = withoutQuery.replace(/\/$/, "");
  return normalized || "/";
}

function routeMatches(currentPath: string, sidebarPath: string) {
  if (!currentPath || !sidebarPath) return false;
  return currentPath === sidebarPath || currentPath.startsWith(`${sidebarPath}/`);
}

function resolveHeaderSidebarTone(
  currentPath: string,
  items: ReturnType<typeof useSidebarNavigation>["primaryRailItems"]
): CircleActionVariant | undefined {
  let bestMatch: { tone: CircleActionVariant; length: number } | undefined;

  for (const item of items) {
    const tone = item.variant ?? item.tone;
    if (!tone) continue;

    const paths = item.matchPaths?.length
      ? item.matchPaths
      : item.path
        ? [item.path]
        : [];

    for (const path of paths) {
      const normalizedPath = normalizeRoutePath(path);
      if (!routeMatches(currentPath, normalizedPath)) continue;

      if (!bestMatch || normalizedPath.length > bestMatch.length) {
        bestMatch = { tone, length: normalizedPath.length };
      }
    }
  }

  return bestMatch?.tone;
}

function getHeaderToneStyle(tone?: CircleActionVariant): CSSProperties | undefined {
  if (!tone) return undefined;
  const style = CIRCLE_ACTION_VARIANT_STYLES[tone];

  return {
    "--page-header-sidebar-accent": style.accentColor,
    "--page-header-sidebar-accent-dark": style.accentColorDark,
    "--page-header-sidebar-shadow-rgb": style.shadowRgb,
  } as CSSProperties;
}

function tintHeaderBadgePills(
  node: React.ReactNode,
  tone?: CircleActionVariant,
  FallbackIcon?: LucideIcon
): React.ReactNode {
  if ((!tone && !FallbackIcon) || node == null) return node;

  return React.Children.map(node, (child) => {
    if (!React.isValidElement(child)) return child;

    const childElement = child as React.ReactElement<any>;

    if (childElement.type === React.Fragment) {
      return React.cloneElement(childElement, {
        children: tintHeaderBadgePills(childElement.props.children, tone, FallbackIcon),
      });
    }

    if (childElement.type === LiquidPill) {
      return React.cloneElement(childElement, {
        ...(tone ? { variant: tone } : {}),
        ...(childElement.props.icon || !FallbackIcon
          ? {}
          : { icon: <FallbackIcon className="h-4 w-4" /> }),
      });
    }

    if (childElement.props?.children) {
      return React.cloneElement(childElement, {
        children: tintHeaderBadgePills(childElement.props.children, tone, FallbackIcon),
      });
    }

    return child;
  });
}

export function PageHeaderV2({
  title,
  description,
  leadingIcon,
  titleSuffix,
  badges,
  showParticles = true,
  particleItems = DEFAULT_PARTICLES,
  particleIcon: ParticleIcon,
  decorativeOnly = false,
  compact = false,
}: PageHeaderV2Props) {
  const router = useRouter();
  const { primaryRailItems } = useSidebarNavigation();
  const currentPath = normalizeRoutePath(router.asPath || router.pathname);
  const sidebarTone = resolveHeaderSidebarTone(currentPath, primaryRailItems);
  const toneStyle = getHeaderToneStyle(sidebarTone);
  const sidebarToneTextClassName = sidebarTone
    ? "text-[var(--page-header-sidebar-accent)] dark:text-[var(--page-header-sidebar-accent-dark)]"
    : "text-slate-500 dark:text-zinc-400";
  const resolvedParticleIconColorClassName = sidebarTone
    ? "page-header-v2-particle--sidebar-tone"
    : "text-slate-400 dark:text-zinc-500";
  const headerBadges = tintHeaderBadgePills(badges, sidebarTone, ParticleIcon);

  return (
    <header
      className={cn(
        "relative overflow-hidden px-1 md:px-0",
        decorativeOnly
          ? compact
            ? "min-h-[11rem] pb-8 pt-20"
            : "pb-2 pt-20"
          : "pb-3 pt-20 md:pb-4"
      )}
      style={toneStyle}
    >
      <div className="page-header-v2-top-gradient" aria-hidden />

      {showParticles && ParticleIcon ? (
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          {particleItems.map((particle, idx) => {
            const particleSize = Math.max(12, Math.round(particle.size * 0.74));

            return (
              <span
                key={`header-icon-particle-${idx}`}
                className={cn("page-header-v2-particle absolute", resolvedParticleIconColorClassName)}
                style={{
                  top: particle.top,
                  left: particle.left,
                  opacity: particle.opacity,
                  ["--phv2-target-opacity" as any]: particle.opacity,
                  ["--phv2-orbit-size" as any]: `${7 + (idx % 4) * 2}px`,
                  ["--phv2-drift-x" as any]: `${idx % 2 === 0 ? 1 : -1}`,
                  ["--phv2-drift-y" as any]: `${idx % 3 === 0 ? 1 : -1}`,
                  ["--phv2-duration" as any]: `${7.4 + idx * 0.72}s`,
                  ["--phv2-delay" as any]: `${idx * 0.28}s`,
                  ["--phv2-enter-delay" as any]: `${500 + idx * 54}ms`,
                }}
              >
                <ParticleIcon size={particleSize} strokeWidth={1.65} />
              </span>
            );
          })}
        </div>
      ) : null}

      {!decorativeOnly ? (
      <div className="relative z-[1]">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-4">
            {leadingIcon ? (
              <div
                className={cn("page-header-v2-leading-icon shrink-0", sidebarToneTextClassName)}
                data-sidebar-tone={sidebarTone ? "true" : undefined}
              >
                {leadingIcon}
              </div>
            ) : null}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 md:gap-3">
                {title ? (
                  <h1 className="text-2xl font-semibold tracking-tight text-slate-950 dark:text-white md:text-4xl">
                    {title}
                  </h1>
                ) : null}
                {titleSuffix}
              </div>
              {description ? (
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-zinc-300 md:text-base">
                  {description}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        {headerBadges ? <div className="mt-5 flex flex-wrap gap-2">{headerBadges}</div> : null}
      </div>
      ) : null}

      <style jsx global>{`
        .page-header-v2-top-gradient {
          pointer-events: none;
          position: absolute;
          left: 50%;
          top: -78px;
          width: min(1120px, 118vw);
          height: 230px;
          transform: translateX(-50%);
          opacity: 0.42;
          background:
            radial-gradient(
              ellipse at 50% 0%,
              rgba(var(--page-header-sidebar-shadow-rgb, 99, 102, 241), 0.24) 0%,
              rgba(var(--page-header-sidebar-shadow-rgb, 99, 102, 241), 0.14) 34%,
              rgba(var(--page-header-sidebar-shadow-rgb, 99, 102, 241), 0.055) 58%,
              transparent 78%
            );
          filter: blur(18px) saturate(1.08);
          mix-blend-mode: multiply;
        }

        .dark .page-header-v2-top-gradient {
          opacity: 0.34;
          mix-blend-mode: screen;
          background:
            radial-gradient(
              ellipse at 50% 0%,
              rgba(var(--page-header-sidebar-shadow-rgb, 99, 102, 241), 0.30) 0%,
              rgba(var(--page-header-sidebar-shadow-rgb, 99, 102, 241), 0.16) 36%,
              rgba(var(--page-header-sidebar-shadow-rgb, 99, 102, 241), 0.06) 60%,
              transparent 80%
            );
        }

        @keyframes page-header-v2-particle-enter {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) translateY(-34px) scale(0.86);
          }
          62% {
            opacity: var(--phv2-target-opacity, 0.2);
            transform: translate(-50%, -50%) translateY(4px) scale(1.02);
          }
          100% {
            opacity: var(--phv2-target-opacity, 0.2);
            transform: translate(-50%, -50%) translateY(0) scale(1);
          }
        }

        @keyframes page-header-v2-particle-orbit {
          0% {
            transform: translate(0, 0) rotate(0deg) scale(0.92);
          }
          18% {
            transform: translate(
              calc(var(--phv2-orbit-size, 9px) * 0.72 * var(--phv2-drift-x, 1)),
              calc(var(--phv2-orbit-size, 9px) * -0.32)
            ) rotate(6deg) scale(1.04);
          }
          38% {
            transform: translate(
              calc(var(--phv2-orbit-size, 9px) * 0.24 * var(--phv2-drift-x, 1)),
              calc(var(--phv2-orbit-size, 9px) * -0.78 * var(--phv2-drift-y, 1))
            ) rotate(-4deg) scale(0.98);
          }
          62% {
            transform: translate(
              calc(var(--phv2-orbit-size, 9px) * -0.56 * var(--phv2-drift-x, 1)),
              calc(var(--phv2-orbit-size, 9px) * 0.38 * var(--phv2-drift-y, 1))
            ) rotate(7deg) scale(1.06);
          }
          82% {
            transform: translate(
              calc(var(--phv2-orbit-size, 9px) * -0.18 * var(--phv2-drift-x, 1)),
              calc(var(--phv2-orbit-size, 9px) * 0.62)
            ) rotate(-3deg) scale(1.01);
          }
          100% {
            transform: translate(0, 0) rotate(0deg) scale(0.92);
          }
        }

        .page-header-v2-leading-icon[data-sidebar-tone="true"],
        .page-header-v2-leading-icon[data-sidebar-tone="true"] * {
          color: var(--page-header-sidebar-accent) !important;
        }

        .page-header-v2-leading-icon[data-sidebar-tone="true"] svg {
          stroke: currentColor !important;
        }

        .dark .page-header-v2-leading-icon[data-sidebar-tone="true"],
        .dark .page-header-v2-leading-icon[data-sidebar-tone="true"] * {
          color: var(--page-header-sidebar-accent-dark) !important;
        }

        .page-header-v2-particle {
          display: inline-flex;
          opacity: 0;
          transform: translate(-50%, -50%);
          animation-name: page-header-v2-particle-enter;
          animation-duration: 980ms;
          animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
          animation-delay: var(--phv2-enter-delay, 0ms);
          animation-fill-mode: both;
          will-change: transform, opacity;
        }

        .page-header-v2-particle > svg {
          animation-name: page-header-v2-particle-orbit;
          animation-duration: var(--phv2-duration, 10s);
          animation-timing-function: cubic-bezier(0.45, 0, 0.2, 1);
          animation-delay: calc(var(--phv2-enter-delay, 0ms) + 760ms + var(--phv2-delay, 0s));
          animation-iteration-count: infinite;
          animation-play-state: running;
          transform-origin: center;
          will-change: transform;
        }

        .page-header-v2-particle--sidebar-tone,
        .page-header-v2-particle--sidebar-tone svg {
          color: var(--page-header-sidebar-accent) !important;
          stroke: currentColor !important;
        }

        .dark .page-header-v2-particle--sidebar-tone,
        .dark .page-header-v2-particle--sidebar-tone svg {
          color: var(--page-header-sidebar-accent-dark) !important;
          stroke: currentColor !important;
        }
      `}</style>
    </header>
  );
}
